export interface Volume {
  no: string;
  title: string;
  tag: string;
  summary: string;
  highlights: string[];
}

export interface Character {
  name: string;
  role: string;
  color: string;
  desc: string;
}

export interface Faq {
  q: string;
  a: string;
}

export interface Chapter {
  tag: string;
  title: string;
  desc: string;
}

export const siteName = "洞洞杂货铺漫画";

export const heroStats = [
  { value: "5", label: "卷主线剧情" },
  { value: "80+", label: "话连载更新" },
  { value: "12", label: "位核心角色" },
  { value: "9.2", label: "读者评分" },
];

export const plotLine = [
  {
    step: "第一章",
    title: "深夜亮起的招牌",
    text: "主角小满在加班的深夜错过末班车，误入一条从未见过的巷子，拐角处的杂货铺亮着暖黄的光，招牌上写着「洞洞杂货铺」五个字。",
  },
  {
    step: "第二章",
    title: "一件物品一段往事",
    text: "杂货铺里没有标价，每一件旧物都寄存着一位客人的执念。小满在一次次的物品交换中，逐渐揭开这家店真正经营的其实不是商品，而是人心。",
  },
  {
    step: "第三章",
    title: "规矩与代价",
    text: "店长阿洞立下规矩：愿望都有代价，等价交换，概不赊欠。小满发现每笔交易背后都藏着一段遗憾，也开始认真思考自己究竟想要什么。",
  },
  {
    step: "第四章",
    title: "杂货铺的秘密",
    text: "当越来越多的客人涌向巷子，杂货铺的真相浮出水面——它每三十年重启一次，而每一任店主都曾是一桩未了心愿的当事人。",
  },
];

export const volumes: Volume[] = [
  {
    no: "第一卷",
    title: "初入巷子",
    tag: "开篇",
    summary:
      "主角小满深夜迷路误入神秘巷子，与店长阿洞相识。本卷以三个独立的小故事开场，借三位客人留下的旧物，把杂货铺「以物易情」的规则一点点带出来，同时埋下阿洞身世的线索。",
    highlights: ["深夜巷子的初遇", "第一件旧物：怀表", "店规的首次立约", "小满为何留下打工"],
  },
  {
    no: "第二卷",
    title: "旧物的回声",
    tag: "成长",
    summary:
      "小满正式成为杂货铺的临时店员，开始接触形形色色的客人。这一卷侧重单元剧，讲述因信物而重逢的亲人、因误会而疏远的挚友，也逐步暗示巷子会「移动」的设定。",
    highlights: ["重逢的信物", "错过的告别", "巷子的第一次移动", "阿洞露出旧伤"],
  },
  {
    no: "第三卷",
    title: "等价交换",
    tag: "转折",
    summary:
      "规则被打破。一位客人企图用别人的代价换取自己的愿望，导致杂货铺陷入危机。小满必须在「守规矩」与「救客人」之间做出选择，剧情节奏明显加快，主线开始收紧。",
    highlights: ["被破坏的店规", "小满的第一次违约", "阿洞的来历揭晓", "幕后之人现身"],
  },
  {
    no: "第四卷",
    title: "三十年的约定",
    tag: "高潮",
    summary:
      "幕后黑手身份揭晓，原来与阿洞三十年前的未了心愿有关。杂货铺迎来三十年一次的重启节点，全员卷入一场关于「遗忘」与「铭记」的赌局，单元剧退场，主线全面展开。",
    highlights: ["店长的身世真相", "三十年重启之日", "全员集结", "愿望的终极代价"],
  },
  {
    no: "第五卷",
    title: "杂货铺仍在营业",
    tag: "结局",
    summary:
      "终局之战落幕，杂货铺的去留与阿洞的愿望得到交代。结尾回到小满的视角，她终于明白：真正经营杂货铺的从来不是规则，而是人与人之间放不下的牵挂。",
    highlights: ["终局赌局", "阿洞的愿望", "小满的选择", "开放式温暖结局"],
  },
];

export const characters: Character[] = [
  {
    name: "小满",
    role: "主角 · 临时店员",
    color: "primary",
    desc: "原本是普通的都市上班族，因深夜迷路误入杂货铺。性格有些笨拙却很真诚，在一次次交易里学会面对自己的遗憾，是整个故事的观察者与推动者。",
  },
  {
    name: "阿洞",
    role: "店主",
    color: "accent",
    desc: "杂货铺的神秘店长，永远穿着旧围裙，总能用几句轻描淡写的话点破客人的心事。看似随性，实则背负着三十年未了的心愿，是全书最大的伏笔。",
  },
  {
    name: "怀表客人",
    role: "卷一登场人物",
    color: "secondary",
    desc: "第一卷留下旧怀表的老人，为等一句迟到三十年的道歉而来。他的故事确立了「旧物承载执念」的核心设定，也第一次让小满感受到杂货铺的重量。",
  },
  {
    name: "苏阿姨",
    role: "巷子常客",
    color: "primary",
    desc: "住在巷口的包子铺老板娘，是少数知道杂货铺存在却从不交易的人。她既是小满生活里的温暖支点，也是第三卷打破规则事件的旁观者和见证者。",
  },
];

export const endings = [
  {
    title: "阿洞的愿望",
    text: "三十年重启之日，众人终于明白阿洞守店的初衷——他并非在「经营」，而是在等一位再也回不来的旧友，用漫长岁月把一句没能说出口的话说完。",
  },
  {
    title: "小满的选择",
    text: "面临唯一一次免费愿望的机会，小满没有为自己求任何东西，而是把机会用来为巷子里所有人了结牵绊。这一选择也让她真正从「客人」变成「家人」。",
  },
  {
    title: "开放式结局",
    text: "最终卷没有给出绝对的句点：杂货铺的门依然在深夜亮着，巷子继续在城市的某个角落移动。作者以「杂货铺仍在营业」收尾，留给读者无尽的想象。",
  },
];

export const faqs: Faq[] = [
  {
    q: "《洞洞杂货铺漫画》讲的到底是什么故事？",
    a: "《洞洞杂货铺漫画》是一部围绕深夜神秘杂货铺展开的奇幻治愈系作品。主角小满误入一条会移动的巷子，在一间没有标价、以「旧物换心愿」的杂货铺里帮忙经营，借一位位客人的故事讲透人心与执念。整体基调温暖治愈，却又带一点淡淡的遗憾。",
  },
  {
    q: "洞洞杂货铺漫画一共有几卷？剧情怎么分？",
    a: "主线共分五卷，分别是《初入巷子》《旧物的回声》《等价交换》《三十年的约定》和《杂货铺仍在营业》。前两卷以单元剧铺陈世界观，第三卷起剧情收紧进入主线，第四卷迎来高潮，第五卷收束结局。",
  },
  {
    q: "洞洞杂货铺漫画的结局是圆满还是遗憾？",
    a: "结局偏温暖开放式。店长阿洞三十年的心愿得以了结，主角小满也做出了自己的选择。故事没有把所有人的遗憾都填平，而是让「放下」与「铭记」并存，作者用一句「杂货铺仍在营业」留下了续写空间。",
  },
  {
    q: "《洞洞杂货铺漫画》适合什么样的读者看？",
    a: "适合喜欢奇幻治愈、单元剧群像和情感细腻作品的读者。如果你偏爱《夏目友人帐》《深夜食堂》这类「一段故事温暖一段人生」的叙事，那么洞洞杂货铺会很对胃口，全龄向、节奏舒缓、台词有味道。",
  },
  {
    q: "在哪里追更洞洞杂货铺漫画的最新章节？",
    a: "目前漫画在有授权的在线阅读平台和官方社交账号同步连载，站内的「追更动态」板块会整理每话的标题与看点。建议以官方发布渠道为准，避免误读剧情或错过临时休刊通知。",
  },
];

export const chapters: Chapter[] = [
  { tag: "第80话", title: "巷子里的最后一场雨", desc: "重启之夜前的短暂平静，小满与苏阿姨聊起「如果愿望免费」。" },
  { tag: "第79话", title: "三十年前的那个雨天", desc: "阿洞的往事回忆篇开启，旧友的轮廓第一次清晰浮现。" },
  { tag: "第78话", title: "全员集结", desc: "幕后黑手身份揭晓，曾经出现过的客人们逐一回到巷子。" },
  { tag: "第77话", title: "被撕掉的页脚", desc: "杂货铺里的账本出现缺失的一页，指向一段被刻意遗忘的交易。" },
  { tag: "第76话", title: "等价交换的代价", desc: "小满为了救下一位客人，第一次真正违反了店规。" },
];

export const navLinks = [
  { label: "漫画简介", href: "#intro" },
  { label: "剧情主线", href: "#story" },
  { label: "分卷剧情", href: "#volumes" },
  { label: "角色介绍", href: "#characters" },
  { label: "结局解析", href: "#ending" },
  { label: "追更动态", href: "#updates" },
];